// @flow

import * as React from 'react';
import { createFragmentContainer, graphql } from 'react-relay/compat';
import { AttributeTable, AttributeTableRow } from 'app/components/shared/AttributeTable';
import AgentBadge from 'app/components/AgentBadge';
import FriendlyTime from 'app/components/shared/FriendlyTime';
import JobEvent from '../JobEvent';
import type { JobEventFinished_event } from './__generated__/JobEventFinished_event.graphql';

type Props = {
  event: JobEventFinished_event,
  previousTimestamp: ?string
};

function JobEventFinished({ event, previousTimestamp }: Props) {
  const { job, actor: { type: actorType } } = event;

  // Refine types here — this sucks a bit because we’re using unions
  // and interfaces and junk for all these types :(
  if (!job) {
    return null;
  }

  switch (actorType) {
    case 'AGENT':
      return (
        <JobEvent timestamp={event.timestamp} previousTimestamp={previousTimestamp}>
          <JobEvent.Label>
            {job.agent ?
              (<AgentBadge agent={job.agent} />) : 'Agent'} Finished Running Job
          </JobEvent.Label>
          <JobEvent.Content>
            <AttributeTable>
              <AttributeTableRow title="Command Exit Status">
                <code className="dark-gray monospace">
                  {event.exitStatus}{job.softFailed ? " (Soft Failed)" : null} {job.exitStatus === "-1" ? ' (Process Killed)' : null}
                </code>
              </AttributeTableRow>
            </AttributeTable>
          </JobEvent.Content>
        </JobEvent>
      );

    case 'SYSTEM':
      // If the job was finished by the system, then the agent was likely lost.
      // We'll pull its details from the job for reference.
      if (job.exitStatus === "-1") {
        return (
          <JobEvent timestamp={event.timestamp} previousTimestamp={previousTimestamp}>
            <JobEvent.Label>
              Dispatcher Cancelled Job
            </JobEvent.Label>
            <JobEvent.Content>
              <AttributeTable>
                {job.agent && job.agent.heartbeatAt ? (
                  <AttributeTableRow title="Last Agent Heartbeat">
                    <FriendlyTime value={job.agent.heartbeatAt} capitalized={true} fractionalSeconds={true} />
                  </AttributeTableRow>
                ) : null}
                <AttributeTableRow title="Command Exit Status">
                  <code className="dark-gray monospace">
                    {event.exitStatus}{job.softFailed ? " (Soft Failed)" : null} (Agent Lost)
                  </code>
                </AttributeTableRow>
              </AttributeTable>
            </JobEvent.Content>
          </JobEvent>
        );
      }

      return (
        <JobEvent timestamp={event.timestamp} previousTimestamp={previousTimestamp}>
          <JobEvent.Label>
            Job Finished by System
          </JobEvent.Label>
          <JobEvent.Content>
            <AttributeTable>
              <AttributeTableRow title="Command Exit Status">
                <code className="dark-gray monospace">
                  {event.exitStatus} {job.softFailed ? "(Soft Failed)" : null}
                </code>
              </AttributeTableRow>
            </AttributeTable>
          </JobEvent.Content>
        </JobEvent>
      );

    default:
      return (
        <JobEvent timestamp={event.timestamp} previousTimestamp={previousTimestamp}>
          <JobEvent.Label>
            Job Finished
          </JobEvent.Label>
          <JobEvent.Content>
            <AttributeTable>
              <AttributeTableRow title="Command Exit Status">
                <code className="dark-gray monospace">
                  {event.exitStatus} {job.softFailed ? "(Soft Failed)" : null}
                </code>
              </AttributeTableRow>
            </AttributeTable>
          </JobEvent.Content>
        </JobEvent>
      );
  }
}

export default createFragmentContainer(JobEventFinished, {
  event: graphql`
    fragment JobEventFinished_event on JobEvent {
      timestamp
      ...on JobEventFinished {
        exitStatus
      }
      actor {
        type
      }
      job {
        softFailed
        exitStatus
        agent {
          ...AgentBadge_agent
          heartbeatAt
        }
      }
    }
  `
});

